/**
 * Tell a camera apart from everything else that lives in the camera catalog.
 *
 * The catalog is harvested per manufacturer, and manufacturers list far more
 * than cameras on the pages we scraped: mounting brackets, PSUs, midspans,
 * multi-camera bundles, licence SKUs, and — most embarrassingly — the quick
 * guides and operating instructions that sit next to the product.
 *
 * Measured on 2026-08-02 across all 65,538 catalog rows, roughly 8% are not
 * cameras. Nothing filtered them, because `getAllCamerasForStaticPages()`
 * only ever checked `connectionType === 'ip'`. So a wall mount got a page at
 * /camera/pelco-sarix-multi-enhanced-pendant-pole-or-wall-mount announcing
 * itself as "8.3MP panoramic, 97°" — specs it does not have and never had,
 * inherited from the catalog's placeholder defaults.
 *
 * That is the failure this module exists to prevent: publishing a spec figure
 * for something that has no such spec. A page is only worth having when the
 * thing it describes is a camera and the numbers on it are real.
 *
 * Deliberately conservative. When a name is ambiguous the answer is 'camera',
 * because dropping a real camera page costs indexed traffic while keeping a
 * stray accessory costs one bad page. False negatives are cheaper here.
 */

export type CatalogItemKind =
  | 'camera'
  | 'recorder'
  | 'mount'
  | 'power'
  | 'bundle'
  | 'documentation'
  | 'license'
  | 'lens';

/** Everything that is not a camera, i.e. must not get a /camera/ page. */
export const NON_CAMERA_KINDS: readonly CatalogItemKind[] = [
  'recorder', 'mount', 'power', 'bundle', 'documentation', 'license', 'lens',
];

/**
 * Ordered rules. First match wins, so the most unambiguous markers come first.
 *
 * Every pattern is anchored on a word boundary. An unanchored /kit/ would
 * match "KITCHEN"; an unanchored /pack/ would match the real Hanwha model
 * "XNP-6400RW PACKAGE"-style names and, worse, any SKU containing "pack".
 */
const RULES: ReadonlyArray<{ kind: CatalogItemKind; re: RegExp }> = [
  // Documentation. The largest group (2,459 rows) and the least ambiguous:
  // no camera is called an "operating instruction".
  { kind: 'documentation', re: /\b(quick[\s-]?(guide|start)|operating[\s-]?instruct\w*|user[\s-]?manual|installation[\s-]?(guide|manual)|datasheet|spec[\s-]?sheet|brochure|leaflet|white[\s-]?paper)\b/i },

  // Licences and software. Two traps, both hit on the first run of this rule:
  //   - "License Plate" is a camera FEATURE (ANPR), not a licence SKU.
  //   - "Plug-In" in a camera name means mains-powered, not a software plugin.
  //     It matched three real cameras: "Arlo Indoor Plug-In (Gen 1)", "Ring
  //     Spotlight Cam Plus (Plug-In)", "Ring Stick Up Cam Plug-In".
  { kind: 'license', re: /\b(licen[cs]e|licencja)\b(?![\s-]*plate)|\b(vms|sdk|subscription)\b|\bsoftware\b/i },

  // Power and network infrastructure.
  { kind: 'power', re: /\b(midspan|injector|power[\s-]?(supply|adapter|adaptor)|psu|transformer|ups|poe[\s-]?switch|network[\s-]?switch|\d+v[\s-]?\d+(\.\d+)?a)\b/i },

  // Mounts, housings and physical accessories. "Corner" and "pendant" are
  // mount styles; a camera name may contain them, so they must co-occur with
  // an explicit accessory word, handled by the alternation below.
  { kind: 'mount', re: /\b(bracket|wall[\s-]?mount|pole[\s-]?mount|pendant[\s-]?(mount|cap)|ceiling[\s-]?mount|surface[\s-]?mount|mount[\s-]?kit|housing|enclosure|sunshield|junction[\s-]?box|back[\s-]?box|adapter[\s-]?plate|tripod)\b/i },

  // A name ENDING in "Mount" is a mount whatever the words before it. Listing
  // mount styles individually kept missing new ones: the style-list rule above
  // knew "Wall Mount" and "Ceiling Mount" but not "Surface Mount" or "PT
  // Mount", so six Pelco and Axis accessories still got camera pages.
  { kind: 'mount', re: /\bmount\s*(\([^)]*\))?\s*$/i },

  // Recorders and storage. These are real products that deserve pages, but at
  // /nvr/, not /camera/.
  { kind: 'recorder', re: /\b(nvr|dvr|xvr|recorder|nas|storage[\s-]?(server|array)|encoder|decoder)\b/i },

  // Multi-unit bundles. A bundle has no single sensor, so no single spec.
  { kind: 'bundle', re: /\b(\d+[\s-]?pack|multi[\s-]?pack|bundle|combo|starter[\s-]?kit|system[\s-]?with[\s-]?\d)\b/i },

  // Standalone lenses only. "Fixed Lens", "Dual-Lens" and "Lens Kit" all appear
  // in genuine camera names describing the camera's own optics — the first run
  // of this rule wrongly caught "Luma 820 … 8MP Turret Fixed Lens" and
  // "SV3C 2K Solar Dual-Lens Kit". So a lens SKU must both mention a lens AND
  // carry no camera marker at all.
  // Note `\w*dome\b` rather than `\bdome\b`: IndigoVision names its cameras
  // "…Internal Minidome Telephoto Lens", and a left word boundary will not
  // match "dome" inside "Minidome", so both of those cameras slipped through.
  {
    kind: 'lens',
    re: /^(?!.*(\b(\d+\s?mp|megapixel|camera|cam|turret|bullet|ptz|fisheye|panoramic|solar|wifi|hd)\b|\w*dome\b)).*\b(varifocal|iris|zoom|telephoto|wide[\s-]?angle)[\s-]?lens\b/i,
  },
];

/**
 * Classify one catalog row by its model name.
 *
 * Name-only on purpose: the spec fields cannot help, because the catalog fills
 * missing values with defaults (hfov 106°, 2.8 mm, 1920x1080 on ~70% of rows),
 * so a bracket and a real camera carry identical numbers. The name is the only
 * honest signal we hold today.
 */
export function classifyCatalogItem(model: string): CatalogItemKind {
  const name = String(model ?? '');
  for (const { kind, re } of RULES) {
    if (re.test(name)) return kind;
  }
  return 'camera';
}

/** True when this row must not get its own /camera/ page. */
export function isNonCamera(model: string): boolean {
  return classifyCatalogItem(model) !== 'camera';
}
